import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Prism from "prismjs";
import "prismjs/themes/prism-solarizedlight.min.css";
import "prismjs/components/prism-go.min.js";
import Seo from "../../components/Seo";

const estimateReadTime = (content) => {
  const wordsPerMinute = 238;
  const wordCount = content.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return minutes <= 1 ? "1 min read" : `${minutes} min read`;
};

const stripHtml = (value = "") =>
  value
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();

const resolveImageUrl = (src) => {
  if (!src) {
    return "https://victorspringer.com/avatar.jpg";
  }

  if (/^https?:\/\//i.test(src)) {
    return src;
  }

  if (src.startsWith("/")) {
    return `https://victorspringer.com${src}`;
  }

  return `https://victorspringer.com/${src.replace(/^\.?\//, "")}`;
};

const extractFirstImage = (html = "") => {
  const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
  return resolveImageUrl(match ? match[1] : "");
};

const resolveAssetUrl = (src) => {
  if (!src) {
    return "/avatar.jpg";
  }

  if (/^https?:\/\//i.test(src) || src.startsWith("/")) {
    return src;
  }

  return `/${src.replace(/^(\.\.\/|\.\/)+/, "")}`;
};

const PostDetailPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await fetch(`/api/blog/${slug}`);
        if (!response.ok) {
          navigate("/not-found");
          return;
        }
        const data = await response.json();
        setPost(data);
      } catch (error) {
        console.error("Error fetching post:", error);
        navigate("/not-found");
      }
    };

    fetchPost();
  }, [slug, navigate]);

  useEffect(() => {
    if (post) {
      Prism.highlightAll();
    }
  }, [post]);

  if (!post) {
    return (
      <p className="text-center">
        <span className="spinner border-2 border-t-2 border-dracula-green w-8 h-8"></span>
      </p>
    );
  }

  const formatTimestamp = (timestamp) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(timestamp._seconds * 1000).toLocaleDateString("en-US", options);
  };

  const articleText = stripHtml(post.content);
  const postImage = extractFirstImage(post.content);
  const publishedIso = post.timestamp ? new Date(post.timestamp._seconds * 1000).toISOString() : "";
  const postSchema = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "@id": `https://victorspringer.com/blog/${slug}`,
    "headline": post.title,
    "description": articleText.slice(0, 160),
    "author": {
      "@type": "Person",
      "name": post.author || "Victor Springer",
      "url": "https://victorspringer.com/cv",
      "image": "https://victorspringer.com/avatar.jpg",
    },
    "datePublished": publishedIso,
    "dateModified": publishedIso,
    "publisher": {
      "@type": "Person",
      "name": "Victor Springer",
      "url": "https://victorspringer.com",
      "image": "https://victorspringer.com/avatar.jpg",
    },
    "mainEntityOfPage": `https://victorspringer.com/blog/${slug}`,
    "url": `https://victorspringer.com/blog/${slug}`,
    "image": postImage,
    "articleBody": articleText
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://victorspringer.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://victorspringer.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title
      }
    ]
  };

  const description = `${post.author || "Victor Springer"}${post.timestamp ? ` - ${formatTimestamp(post.timestamp)}` : ""} - ${estimateReadTime(post.content)}. ${articleText.slice(0, 110)}`;

  return (
    <>
      <Seo
        title={post.title}
        description={description}
        type="article"
        image={postImage}
        canonicalPath={`/blog/${slug}`}
        structuredData={[postSchema, breadcrumbSchema]}
        author={post.author || "Victor Springer"}
        publishedTime={publishedIso || undefined}
        modifiedTime={publishedIso || undefined}
        section="Software Engineering"
      />

      <main className="max-w-4xl mx-auto">
        <article className="md:col-span-2" itemScope itemType="https://schema.org/BlogPosting">
          <h1 className="text-center text-dracula-pink text-4xl font-semibold mb-6" itemProp="headline">{post.title}<span className="cursor"></span></h1>

          {post.author && (
            <div className="flex items-center text-dracula-comment text-sm mb-6" itemProp="author" itemScope itemType="https://schema.org/Person">
              <img
                src={resolveAssetUrl(post.authorProfilePicture)}
                alt={post.author}
                width="44"
                height="44"
                loading="lazy"
                decoding="async"
                className="w-11 h-11 rounded-full mr-4"
                itemProp="image"
              />
              <div className="flex flex-col items-start">
                <p className="text-dracula-green" itemProp="name">{post.author}</p>
                <p>
                  {estimateReadTime(post.content)}
                  <span className="mx-2">·</span>
                  {post.timestamp && publishedIso && (
                    <time dateTime={publishedIso} itemProp="datePublished">{formatTimestamp(post.timestamp)}</time>
                  )}
                </p>
              </div>
            </div>
          )}

          <div
            className="blog-content text-dracula-foreground mt-4"
            itemProp="articleBody"
            dangerouslySetInnerHTML={{ __html: post.content }}
          />

          <div className="text-center text-sm mt-12">
            <a
              href="/blog"
              className="text-dracula-cyan text-sm mt-3 inline-block hover:text-dracula-green font-bold"
            >
              ← See all posts
            </a>
          </div>
        </article>
      </main>
    </>
  );
};

export default PostDetailPage;
